// Asistente IA del cotizador — CEREBRO ÚNICO: Alex (FluxAI AgenteFluxAI).
// Solar Hub está cableado DIRECTO a FluxAI. `hubPost('ai-recommend')` pega a hub-api
// (`/hub-api/ai-recommend` → `server/routes/ia.js` → `/internal/agente/recomendar-pv`).
// Cascada Groq → Gemini → Claude solo como respaldo. 
// Contrato POST /hub-api/ai-recommend:
//   Input: {
//     context: 'sizing' | 'review' | 'explain',
//     payload: { ...anything the tool wants to send... }
//   }
//   Server orquesta el modelo (configurable) y devuelve JSON estricto.
//   Output: {
//     summary: string,         // 1–2 frases
//     findings: string[],      // observaciones clave
//     warnings: string[],      // alertas técnicas/normativas
//     suggestions: string[],   // acciones recomendadas (texto libre)
//     actions: [{ field, value, label, reason }],  // cambios programáticos al estado
//     provider?: string,
//     tokens?: { in, out }
//   }

import { hubPost, hubConfigurado } from './hub';
import { getToken } from './auth';
import { ALEX_NOMBRE, conAlex } from './alex';
import { comisionSuite } from './pagos';
import { COMISION_LOCAL } from './comision';

// Campos del estado del cotizador que pueden mutarse desde una `action` IA.
//
// 🔴 `monthlyKwh` NO está, y es deliberado (RD-8): ese dato viene de la FACTURA del usuario y de él
// cuelga todo el ROI —kWp, cobertura, ahorro, payback—. `CLAUDE.md` lo documentaba como excluido
// desde siempre; en realidad estaba en esta lista Y en la del workflow, así que el modelo podía
// reescribir el consumo real del cliente y el botón «Aplicar mejoras» lo aceptaba.
//
// El filtro se conserva acá aunque el backend propio ya sanee (`server/routes/ia.js`): mientras la
// cascada siga cayendo al respaldo, el workflow VIEJO puede seguir proponiendo `monthlyKwh` — y este
// filtro es lo único que lo detiene en ese camino.
export const APPLYABLE_FIELDS = [
  'systemType', 'battQty', 'busVoltage',
  'backupHours', 'autonomyDays', 'criticalPct',
  'acometida', 'availableArea', 'wantsExcedentes',
];

export function aiConfigured() { return hubConfigurado(); }

/**
 * 💳 El refinamiento con IA es PAGO (dueño, 8-oct-2026: «el cotizador gratuito no trae
 * refinamiento IA; se debe pagar»). La sesión viaja en el cuerpo para que el servidor sepa quién
 * pregunta; sin plan pago responde 402 y NO llama a ninguna IA. Ese 402 no es una caída: vuelve
 * como `{ ok:false, reason:'ia_de_pago' }` para que el cotizador muestre el aviso y el botón.
 * El enlace solo puede ser una ruta PROPIA: el cuerpo viene de la red.
 */
export function avisoDePago(e) {
  if (!e || e.status !== 402) return null;
  const d = (e.data && typeof e.data === 'object') ? e.data : {};
  const propia = typeof d.contratar_url === 'string' && d.contratar_url.startsWith('/') && !d.contratar_url.startsWith('//');
  return {
    ok: false,
    reason: 'ia_de_pago',
    motivo: d.motivo || 'Refinamiento con IA: disponible pagando.',
    contratar_url: propia ? d.contratar_url : '/?view=estudio',
  };
}

/** El 402 de plan vuelve como el aviso; cualquier otro fallo sigue siendo un fallo. */
function pagoOLanza(e) {
  const pago = avisoDePago(e);
  if (pago) return pago;
  throw e;
}

export async function aiRecommend(context, payload) {
  // Alex = agente ÚNICO de la suite Alebas. El workflow viejo delega en AgenteFluxAI (FluxAI) → el
  // MISMO cerebro que el chat de Alex; si FluxAI no está configurado/responde, cae a la cascada gratis
  // (que igual lleva la identidad/persona de Alex). Campos aditivos → el respaldo los ignora si no los cablea.
  // 🔑 El reparto del técnico se PREGUNTA antes de armar la persona: Alex habla con el técnico por
  // WhatsApp, y un porcentaje viejo dicho con seguridad es peor que no decirlo. Si no llega, se usa
  // el 20/80 local — el mismo que estaba escrito en el prompt hasta el 7-sep.
  let comision = COMISION_LOCAL;
  try {
    const r = await comisionSuite('instalacion');
    if (r && r.ok !== false && Number.isFinite(Number(r.pct))) comision = r;
  } catch { /* la recomendación no se cae porque no se sepa el porcentaje */ }
  // 💳 El refinamiento con IA es PAGO: la sesión viaja en el cuerpo y el 402 vuelve como aviso
  // (ver `avisoDePago` arriba), no como caída.
  const tok = getToken();
  const cuerpo = { context, payload, agente: ALEX_NOMBRE, persona: conAlex('', comision), ...(tok ? { token: tok } : {}) };
  const data = await hubPost('ai-recommend', cuerpo).catch(pagoOLanza);
  if (data?.reason === 'ia_de_pago') return data;
  if (!data || typeof data !== 'object') throw new Error('Respuesta inválida (ai-recommend)');
  const allowed = new Set(APPLYABLE_FIELDS);
  const rawActions = Array.isArray(data.actions) ? data.actions : [];
  const actions = rawActions
    .filter(a => a && typeof a === 'object' && allowed.has(a.field))
    .map(a => ({
      field: String(a.field),
      value: a.value,
      label: typeof a.label === 'string' ? a.label : '',
      reason: typeof a.reason === 'string' ? a.reason : '',
    }));
  return {
    summary: data.summary || '',
    findings: Array.isArray(data.findings) ? data.findings : [],
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
    suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
    actions,
    provider: data.provider || null,
    tokens: data.tokens || null,
  };
}
